import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../types/telegram';
import { Message } from './Message';
import { MessageInput } from './MessageInput';

interface ChatProps {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  onCallback: (data: string, message: ChatMessage) => void;
  onClear: () => void;
  disabled?: boolean;
  pendingAlert: { text: string; showAlert: boolean } | null;
  onDismissAlert: () => void;
}

export function Chat({
  messages,
  onSend,
  onCallback,
  onClear,
  disabled = false,
  pendingAlert,
  onDismissAlert,
}: ChatProps): React.JSX.Element {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-dismiss toast alerts
  useEffect(() => {
    if (pendingAlert && !pendingAlert.showAlert) {
      const timer = setTimeout(onDismissAlert, 2000);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [pendingAlert, onDismissAlert]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-telegram-bg-dark">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-telegram-blue text-white">
            {'\uD83E\uDD16'}
          </div>
          <div>
            <div className="font-medium">Test Bot</div>
            <div className="text-xs text-telegram-text-secondary dark:text-telegram-text-secondary-dark">
              {disabled ? 'Offline' : 'Online'}
            </div>
          </div>
        </div>
        <button
          onClick={onClear}
          className="rounded-md px-3 py-1 text-sm text-telegram-text-secondary hover:bg-telegram-secondary-bg dark:text-telegram-text-secondary-dark dark:hover:bg-telegram-secondary-bg-dark"
          title="Clear chat history"
        >
          {'\uD83D\uDDD1\uFE0F'} Clear
        </button>
      </div>

      {/* Messages */}
      <div className="scrollbar-thin flex-1 overflow-y-auto bg-telegram-secondary-bg/30 p-4 dark:bg-telegram-bg-dark">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-telegram-text-secondary dark:text-telegram-text-secondary-dark">
            <div className="text-center">
              <div className="mb-2 text-4xl">{'\uD83D\uDCAC'}</div>
              <p>No messages yet</p>
              <p className="text-sm">Send a message to start testing</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => (
              <Message
                key={message.message_id}
                message={message}
                onCallback={onCallback}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Callback Alert (Toast) */}
      {pendingAlert && !pendingAlert.showAlert && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg dark:bg-gray-700">
          {pendingAlert.text}
        </div>
      )}

      {/* Callback Alert (Modal) */}
      {pendingAlert !== null && pendingAlert.showAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-sm rounded-lg bg-white p-4 shadow-xl dark:bg-telegram-secondary-bg-dark">
            <p className="mb-4 text-center">{pendingAlert.text}</p>
            <button
              onClick={onDismissAlert}
              className="w-full rounded-md bg-telegram-blue px-4 py-2 text-white hover:bg-telegram-blue-dark"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <MessageInput onSend={onSend} disabled={disabled} />
    </div>
  );
}
